/**
 * FINANCE ADMIN - APPROVAL QUEUE PAGE
 * 
 * Company-wide approval oversight for Finance Admins:
 * 1. All Pending - Every invoice awaiting approval across the company
 * 2. My Tasks - Invoices personally assigned to this user
 * 3. History - Recently approved/rejected invoices
 * 
 * Integrates InvoiceApprovalDetail modal for GL/Budget assignment + AI suggestions
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import apiClient from '../api/client';
import { usePermissions } from '../context/PermissionsContext';
import { 
  CheckCircle, Clock, AlertCircle, XCircle, 
  Eye, FileText, Banknote, Wallet, TrendingUp, Shield,
  ArrowUpDown, ArrowUp, ArrowDown, Search, Columns3, Check, X
} from 'lucide-react';
import InvoiceApprovalDetail from '../components/InvoiceApprovalDetail';
import TablePagination from '../components/TablePagination';
import SortableTh from '../components/SortableTh';

const PENDING_PAGE_SIZE = 25;

// ── Column definitions ─────────────────────────────────────────────────────
const PENDING_COLS = [
  { key: 'invoiceNumber', label: 'Invoice #',  locked: true },
  { key: 'vendor',        label: 'Vendor' },
  { key: 'department',    label: 'Department' },
  { key: 'amount',        label: 'Amount' },
  { key: 'date',          label: 'Date' },
  { key: 'status',        label: 'Status' },
  { key: 'approver',      label: 'Approver' },
  { key: 'actions',       label: 'Actions',   locked: true },
];
const PENDING_COL_DEFAULTS = PENDING_COLS.map(c => c.key);

const HISTORY_COLS = [
  { key: 'invoiceNumber', label: 'Invoice #',  locked: true },
  { key: 'vendor',        label: 'Vendor' },
  { key: 'department',    label: 'Department' },
  { key: 'amount',        label: 'Amount' },
  { key: 'decision',      label: 'Decision' },
  { key: 'approver',      label: 'Approver' },
  { key: 'date',          label: 'Date' },
  { key: 'actions',       label: 'Actions',   locked: true },
];
const HISTORY_COL_DEFAULTS = HISTORY_COLS.map(c => c.key);

const PENDING_COL_LS_KEY = 'approvalQueue_pendingCols';
const HISTORY_COL_LS_KEY = 'approvalQueue_historyCols';

function loadColPrefs(key, defaults) {
  try {
    const saved = localStorage.getItem(key);
    if (saved) {
      const parsed = JSON.parse(saved);
      // always include locked columns
      const locked = defaults.filter(k => {
        const allCols = [...PENDING_COLS, ...HISTORY_COLS];
        return allCols.find(c => c.key === k)?.locked;
      });
      return [...new Set([...locked, ...parsed])];
    }
  } catch {}
  return defaults;
}

export default function ApprovalQueue() {
  const { hasPermission } = usePermissions();
  const [companyQueue, setCompanyQueue] = useState([]);
  const [myTasks, setMyTasks] = useState([]);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState({ PendingCount: 0, ApprovedToday: 0, RejectedToday: 0, PendingTotalValue: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('all-pending');
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPagination, setHistoryPagination] = useState({ total: 0, totalPages: 1 });
  const [pendingPage, setPendingPage] = useState(1);
  const [takeoverModal, setTakeoverModal] = useState(null);
  const [takeoverReason, setTakeoverReason] = useState('');
  const [takeoverSubmitting, setTakeoverSubmitting] = useState(false);
  const [sortField, setSortField] = useState('InvoiceDate');
  const [sortDirection, setSortDirection] = useState('desc');

  // Search / filter / column visibility
  const [search, setSearch] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [filterVendor, setFilterVendor] = useState('');
  const [filterDecision, setFilterDecision] = useState('');
  const [histSortField, setHistSortField] = useState('ActionDate');
  const [histSortDir, setHistSortDir] = useState('desc');
  const [colVisibility, setColVisibility] = useState({
    pending: loadColPrefs(PENDING_COL_LS_KEY, PENDING_COL_DEFAULTS),
    history: loadColPrefs(HISTORY_COL_LS_KEY, HISTORY_COL_DEFAULTS),
  });
  const [showColPicker, setShowColPicker] = useState(false);
  const colPickerRef = useRef(null);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />;
    return sortDirection === 'asc'
      ? <ArrowUp className="w-3 h-3 ml-1 text-blue-600" />
      : <ArrowDown className="w-3 h-3 ml-1 text-blue-600" />;
  };

  const sortRows = (rows) => {
    return [...rows].sort((a, b) => {
      let aVal = a[sortField], bVal = b[sortField];
      if (sortField === 'TotalAmount') { aVal = Number(aVal) || 0; bVal = Number(bVal) || 0; }
      else if (sortField === 'InvoiceDate') { aVal = new Date(aVal || 0); bVal = new Date(bVal || 0); }
      else { aVal = String(aVal || '').toLowerCase(); bVal = String(bVal || '').toLowerCase(); }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleHistSort = (field) => {
    if (histSortField === field) setHistSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setHistSortField(field); setHistSortDir('asc'); }
  };

  const HistSortIcon = ({ field }) => {
    if (histSortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />;
    return histSortDir === 'asc'
      ? <ArrowUp className="w-3 h-3 ml-1 text-blue-600" />
      : <ArrowDown className="w-3 h-3 ml-1 text-blue-600" />;
  };

  // Column visibility helpers
  const col = (scope, key) => (colVisibility[scope] || []).includes(key);
  const colCount = (scope) => (colVisibility[scope] || []).filter(k => {
    const allCols = scope === 'pending' ? PENDING_COLS : HISTORY_COLS;
    return allCols.find(c => c.key === k);
  }).length;

  const toggleCol = (scope, key) => {
    const defs = scope === 'pending' ? PENDING_COLS : HISTORY_COLS;
    const lsKey = scope === 'pending' ? PENDING_COL_LS_KEY : HISTORY_COL_LS_KEY;
    if (defs.find(c => c.key === key)?.locked) return;
    setColVisibility(prev => {
      const current = prev[scope] || [];
      const next = current.includes(key) ? current.filter(k => k !== key) : [...current, key];
      localStorage.setItem(lsKey, JSON.stringify(next));
      return { ...prev, [scope]: next };
    });
  };

  const resetCols = (scope) => {
    const lsKey = scope === 'pending' ? PENDING_COL_LS_KEY : HISTORY_COL_LS_KEY;
    const defaults = scope === 'pending' ? PENDING_COL_DEFAULTS : HISTORY_COL_DEFAULTS;
    localStorage.setItem(lsKey, JSON.stringify(defaults));
    setColVisibility(prev => ({ ...prev, [scope]: defaults }));
  };

  // Close column picker on outside click
  useEffect(() => {
    const handler = (e) => {
      if (colPickerRef.current && !colPickerRef.current.contains(e.target)) setShowColPicker(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (activeTab === 'history') {
      loadHistory();
    }
  }, [activeTab, historyPage]);

  // Reset pending page when search or filters change
  useEffect(() => {
    setPendingPage(1);
  }, [search, filterDept, filterVendor]);

  if (!hasPermission('approve_invoices')) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600 font-semibold">Access Denied</p>
        <p className="text-gray-500 mt-2">You do not have permission to view the approval queue.</p>
      </div>
    );
  }

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [companyRes, myTasksRes] = await Promise.all([
        apiClient.get('/approvals/company-queue'),
        apiClient.get('/approvals/my-queue')
      ]);

      setCompanyQueue(companyRes.data.invoices || []);
      setStats(companyRes.data.stats || stats);
      setMyTasks(myTasksRes.data.invoices || []);
    } catch (err) {
      console.error('Failed to load approval queue:', err);
      setError(err.response?.data?.error || 'Failed to load approval queue');
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const res = await apiClient.get('/approvals/company-history', {
        params: { page: historyPage, limit: 50 }
      });
      setHistory(res.data.approvals || []);
      setHistoryPagination(res.data.pagination || { total: 0, totalPages: 1 });
    } catch (err) {
      console.error('Failed to load history:', err);
    }
  };

  const handleTakeover = async () => {
    if (!takeoverReason.trim()) {
      alert('Please provide a reason for the takeover');
      return;
    }
    try {
      setTakeoverSubmitting(true);
      await apiClient.post(`/approvals/${takeoverModal.invoiceId}/finance-takeover`, {
        reason: takeoverReason.trim()
      });
      setTakeoverModal(null);
      setTakeoverReason('');
      loadData();
    } catch (err) {
      console.error('Takeover error:', err);
      alert(err.response?.data?.error || 'Failed to take over invoice');
    } finally {
      setTakeoverSubmitting(false);
    }
  };

  const formatCurrency = (amount, currency = 'GBP') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const styles = {
      'Pending': 'bg-yellow-100 text-yellow-800',
      'Pending Approval': 'bg-yellow-100 text-yellow-800',
      'Processing': 'bg-blue-100 text-blue-800',
      'Approved': 'bg-green-100 text-green-800',
      'Rejected': 'bg-red-100 text-red-800',
      'Exported': 'bg-indigo-100 text-indigo-800',
      'Posted': 'bg-emerald-100 text-emerald-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  const renderToolbar = (scope, rows, extraFilters = null) => {
    const allCols = scope === 'pending' ? PENDING_COLS : HISTORY_COLS;
    return (
      <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search invoice #, vendor, department..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-300 focus:border-blue-400"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Extra filters injected per scope */}
        {extraFilters}

        {/* Result count */}
        <span className="text-xs text-gray-500 whitespace-nowrap ml-auto">{rows.length} result{rows.length !== 1 ? 's' : ''}</span>

        {/* Column picker */}
        <div className="relative" ref={colPickerRef}>
          <button
            onClick={() => setShowColPicker(p => !p)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm border rounded-lg transition-colors ${showColPicker ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'}`}
            title="Show/hide columns"
          >
            <Columns3 className="w-4 h-4" />
            Columns
          </button>
          {showColPicker && (
            <div className="absolute right-0 top-10 z-50 w-52 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden">
              <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Columns</span>
                <button onClick={() => resetCols(scope)} className="text-xs text-blue-600 hover:underline">Reset</button>
              </div>
              <div className="py-1 max-h-64 overflow-y-auto">
                {allCols.map(c => (
                  <button
                    key={c.key}
                    onClick={() => toggleCol(scope, c.key)}
                    disabled={c.locked}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-default transition-colors"
                  >
                    <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${col(scope, c.key) ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                      {col(scope, c.key) && <Check className="w-3 h-3 text-white" />}
                    </span>
                    {c.label}
                    {c.locked && <span className="ml-auto text-[10px] text-gray-400">locked</span>}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSummaryCards = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <div className="bg-white rounded-lg shadow p-4 border-l-4 border-amber-500">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Pending Approvals</p>
            <p className="text-2xl font-bold text-gray-900">{stats.PendingCount}</p>
          </div>
          <Clock className="w-8 h-8 text-amber-500" />
        </div>
      </div>
      <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Approved Today</p>
            <p className="text-2xl font-bold text-gray-900">{stats.ApprovedToday}</p>
          </div>
          <CheckCircle className="w-8 h-8 text-green-500" />
        </div>
      </div>
      <div className="bg-white rounded-lg shadow p-4 border-l-4 border-red-500">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Rejected Today</p>
            <p className="text-2xl font-bold text-gray-900">{stats.RejectedToday}</p>
          </div>
          <XCircle className="w-8 h-8 text-red-500" />
        </div>
      </div>
      <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Pending Value</p>
            <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.PendingTotalValue)}</p>
          </div>
          <Wallet className="w-8 h-8 text-blue-500" />
        </div>
      </div>
    </div>
  );

  const renderPendingTable = (rawInvoices, depts, vendors, opts = {}) => {
    // Apply search + filters
    const q = search.toLowerCase();
    const filtered = rawInvoices.filter(inv => {
      const matchSearch = !q
        || (inv.InvoiceNumber || '').toLowerCase().includes(q)
        || (inv.VendorName || '').toLowerCase().includes(q)
        || (inv.DepartmentName || '').toLowerCase().includes(q);
      const matchDept = !filterDept || inv.DepartmentName === filterDept;
      const matchVendor = !filterVendor || inv.VendorName === filterVendor;
      return matchSearch && matchDept && matchVendor;
    });
    const sorted = sortRows(filtered);
    const totalFiltered = sorted.length;
    const pagedSorted = sorted.slice((pendingPage - 1) * PENDING_PAGE_SIZE, pendingPage * PENDING_PAGE_SIZE);

    const extraFilters = (
      <>
        <select
          value={filterDept}
          onChange={e => setFilterDept(e.target.value)}
          className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-300 focus:border-blue-400 bg-white"
        >
          <option value="">All Departments</option>
          {depts.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <select
          value={filterVendor}
          onChange={e => setFilterVendor(e.target.value)}
          className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-300 focus:border-blue-400 bg-white"
        >
          <option value="">All Vendors</option>
          {vendors.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
      </>
    );

    const visibleColCount = ['invoiceNumber','vendor','department','amount','date','status','approver','actions'].filter(k => col('pending', k)).length;

    return (
      <>
        <div>
        {renderToolbar('pending', sorted, extraFilters)}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                {col('pending','invoiceNumber') && <SortableTh field="InvoiceNumber" sortBy={sortField} sortDir={sortDirection} onSort={handleSort}>Invoice #</SortableTh>}
                {col('pending','vendor') && <SortableTh field="VendorName" sortBy={sortField} sortDir={sortDirection} onSort={handleSort}>Vendor</SortableTh>}
                {col('pending','department') && <SortableTh field="DepartmentName" sortBy={sortField} sortDir={sortDirection} onSort={handleSort}>Department</SortableTh>}
                {col('pending','amount') && <SortableTh field="TotalAmount" sortBy={sortField} sortDir={sortDirection} onSort={handleSort} align="right">Amount</SortableTh>}
                {col('pending','date') && <SortableTh field="InvoiceDate" sortBy={sortField} sortDir={sortDirection} onSort={handleSort}>Date</SortableTh>}
                {col('pending','status') && <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>}
                {col('pending','approver') && <SortableTh field="CurrentApproverName" sortBy={sortField} sortDir={sortDirection} onSort={handleSort}>Approver</SortableTh>}
                {col('pending','actions') && <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={visibleColCount} className="px-4 py-8 text-center text-gray-500">
                    {search || filterDept || filterVendor ? 'No invoices match your filters' : 'No pending invoices'}
                  </td>
                </tr>
              ) : (
                pagedSorted.map((invoice) => (
                  <tr key={invoice.InvoiceID + '-' + (invoice.ApprovalID || '')} className="hover:bg-gray-50">
                    {col('pending','invoiceNumber') && <td className="px-4 py-3 whitespace-nowrap"><div className="text-sm font-medium text-gray-900">{invoice.InvoiceNumber}</div></td>}
                    {col('pending','vendor') && <td className="px-4 py-3 whitespace-nowrap"><div className="text-sm text-gray-900">{invoice.VendorName || '—'}</div></td>}
                    {col('pending','department') && <td className="px-4 py-3 whitespace-nowrap"><div className="text-sm text-gray-600">{invoice.DepartmentName || '—'}</div></td>}
                    {col('pending','amount') && <td className="px-4 py-3 whitespace-nowrap text-right"><div className="text-sm font-semibold text-gray-900">{formatCurrency(invoice.TotalAmount, invoice.Currency)}</div></td>}
                    {col('pending','date') && <td className="px-4 py-3 whitespace-nowrap"><div className="text-sm text-gray-500">{invoice.InvoiceDate ? new Date(invoice.InvoiceDate).toLocaleDateString('en-GB') : '—'}</div></td>}
                    {col('pending','status') && <td className="px-4 py-3 whitespace-nowrap">{getStatusBadge(invoice.Status || invoice.ApprovalStatus)}</td>}
                    {col('pending','approver') && (
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {invoice.CurrentApproverName || 'Unassigned'}
                          {invoice.IsTakenOver ? (
                            <span className="ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800" title={`Taken over from ${invoice.OriginalApproverName}. Reason: ${invoice.TakeoverReason}`}>
                              <Shield className="w-3 h-3" /> Taken Over
                            </span>
                          ) : null}
                        </div>
                      </td>
                    )}
                    {col('pending','actions') && (
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-2">
                          {!invoice.IsTakenOver && !opts.hideTakeover && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setTakeoverModal({ invoiceId: invoice.InvoiceID, invoiceNumber: invoice.InvoiceNumber, currentApprover: invoice.CurrentApproverName }); }}
                              className="text-purple-600 hover:text-purple-900 font-medium inline-flex items-center gap-1 text-sm"
                              title="Take over as Finance Admin"
                            >
                              <Shield className="w-4 h-4" />
                              Take Over
                            </button>
                          )}
                          <button
                            onClick={() => setSelectedInvoiceId(invoice.InvoiceID)}
                            className="text-blue-600 hover:text-blue-900 font-medium inline-flex items-center gap-1 text-sm"
                          >
                            <Eye className="w-4 h-4" />
                            Review
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <TablePagination
        page={pendingPage}
        pageSize={PENDING_PAGE_SIZE}
        total={totalFiltered}
        onPageChange={setPendingPage}
      />
      </>
    );
  };

  const renderHistoryTable = () => {
    // Apply search + decision filter, then sort
    const q = search.toLowerCase();
    const filtered = history.filter(item => {
      const matchSearch = !q
        || (item.InvoiceNumber || '').toLowerCase().includes(q)
        || (item.VendorName || '').toLowerCase().includes(q)
        || (item.DepartmentName || '').toLowerCase().includes(q)
        || (item.ApproverName || '').toLowerCase().includes(q);
      const matchDecision = !filterDecision || item.ApprovalStatus === filterDecision;
      return matchSearch && matchDecision;
    });

    const sortedHist = [...filtered].sort((a, b) => {
      let aVal = a[histSortField], bVal = b[histSortField];
      if (histSortField === 'TotalAmount') { aVal = Number(aVal) || 0; bVal = Number(bVal) || 0; }
      else if (histSortField === 'ActionDate') { aVal = new Date(aVal || 0); bVal = new Date(bVal || 0); }
      else { aVal = String(aVal || '').toLowerCase(); bVal = String(bVal || '').toLowerCase(); }
      if (aVal < bVal) return histSortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return histSortDir === 'asc' ? 1 : -1;
      return 0;
    });

    // Unique decisions for filter dropdown
    const decisions = [...new Set(history.map(h => h.ApprovalStatus).filter(Boolean))];

    const extraFilters = (
      <select
        value={filterDecision}
        onChange={e => setFilterDecision(e.target.value)}
        className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-300 focus:border-blue-400 bg-white"
      >
        <option value="">All Decisions</option>
        {decisions.map(d => <option key={d} value={d}>{d}</option>)}
      </select>
    );

    const visibleColCount = ['invoiceNumber','vendor','department','amount','decision','approver','date','actions'].filter(k => col('history', k)).length;

    return (
      <div>
        {renderToolbar('history', sortedHist, extraFilters)}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                {col('history','invoiceNumber') && <SortableTh field="InvoiceNumber" sortBy={histSortField} sortDir={histSortDir} onSort={handleHistSort}>Invoice #</SortableTh>}
                {col('history','vendor') && <SortableTh field="VendorName" sortBy={histSortField} sortDir={histSortDir} onSort={handleHistSort}>Vendor</SortableTh>}
                {col('history','department') && <SortableTh field="DepartmentName" sortBy={histSortField} sortDir={histSortDir} onSort={handleHistSort}>Department</SortableTh>}
                {col('history','amount') && <SortableTh field="TotalAmount" sortBy={histSortField} sortDir={histSortDir} onSort={handleHistSort} align="right">Amount</SortableTh>}
                {col('history','decision') && <SortableTh field="ApprovalStatus" sortBy={histSortField} sortDir={histSortDir} onSort={handleHistSort}>Decision</SortableTh>}
                {col('history','approver') && <SortableTh field="ApproverName" sortBy={histSortField} sortDir={histSortDir} onSort={handleHistSort}>Approver</SortableTh>}
                {col('history','date') && <SortableTh field="ActionDate" sortBy={histSortField} sortDir={histSortDir} onSort={handleHistSort}>Date</SortableTh>}
                {col('history','actions') && <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sortedHist.length === 0 ? (
                <tr>
                  <td colSpan={visibleColCount} className="px-4 py-8 text-center text-gray-500">
                    {search || filterDecision ? 'No records match your filters' : 'No approval history found'}
                  </td>
                </tr>
              ) : (
                sortedHist.map((item) => (
                  <tr key={item.ApprovalID} className="hover:bg-gray-50">
                    {col('history','invoiceNumber') && <td className="px-4 py-3 whitespace-nowrap"><div className="text-sm font-medium text-gray-900">{item.InvoiceNumber}</div></td>}
                    {col('history','vendor') && <td className="px-4 py-3 whitespace-nowrap"><div className="text-sm text-gray-900">{item.VendorName || '—'}</div></td>}
                    {col('history','department') && <td className="px-4 py-3 whitespace-nowrap"><div className="text-sm text-gray-600">{item.DepartmentName || '—'}</div></td>}
                    {col('history','amount') && <td className="px-4 py-3 whitespace-nowrap text-right"><div className="text-sm font-semibold text-gray-900">{formatCurrency(item.TotalAmount, item.Currency)}</div></td>}
                    {col('history','decision') && <td className="px-4 py-3 whitespace-nowrap">{getStatusBadge(item.ApprovalStatus)}</td>}
                    {col('history','approver') && <td className="px-4 py-3 whitespace-nowrap"><div className="text-sm text-gray-900">{item.ApproverName || '—'}</div></td>}
                    {col('history','date') && <td className="px-4 py-3 whitespace-nowrap"><div className="text-sm text-gray-500">{item.ActionDate ? new Date(item.ActionDate).toLocaleDateString('en-GB') : '—'}</div></td>}
                    {col('history','actions') && (
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <button
                          onClick={() => setSelectedInvoiceId(item.InvoiceID)}
                          className="text-blue-600 hover:text-blue-900 font-medium inline-flex items-center gap-1 text-sm"
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <TablePagination
          page={historyPage}
          pageSize={50}
          total={historyPagination.total}
          onPageChange={setHistoryPage}
        />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-500">Loading approval queue...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <CheckCircle className="w-7 h-7 text-blue-600" />
          Invoice Approval Queue
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Company-wide approval oversight — review, assign GL codes, and approve or reject invoices
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
        </div>
      )}

      {renderSummaryCards()}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-4">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => { setActiveTab('all-pending'); setSearch(''); setFilterDept(''); setFilterVendor(''); }}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'all-pending'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FileText className="w-4 h-4 inline mr-1" />
            All Pending ({companyQueue.length})
          </button>
          <button
            onClick={() => { setActiveTab('my-tasks'); setSearch(''); setFilterDept(''); setFilterVendor(''); }}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'my-tasks'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Clock className="w-4 h-4 inline mr-1" />
            My Tasks ({myTasks.length})
          </button>
          <button
            onClick={() => { setActiveTab('history'); setSearch(''); setFilterDecision(''); if (history.length === 0) loadHistory(); }}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'history'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <TrendingUp className="w-4 h-4 inline mr-1" />
            History
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow">
        {activeTab === 'all-pending' && (() => {
          const depts = [...new Set(companyQueue.map(i => i.DepartmentName).filter(Boolean))].sort();
          const vendors = [...new Set(companyQueue.map(i => i.VendorName).filter(Boolean))].sort();
          return (
            <div>
              <div className="px-4 py-3 bg-blue-50 border-b border-blue-100">
                <p className="text-sm text-blue-800">
                  <strong>All Pending:</strong> Invoices awaiting approval — review, assign GL codes, and take action
                </p>
              </div>
              {renderPendingTable(companyQueue, depts, vendors)}
            </div>
          );
        })()}

        {activeTab === 'my-tasks' && (() => {
          const depts = [...new Set(myTasks.map(i => i.DepartmentName).filter(Boolean))].sort();
          const vendors = [...new Set(myTasks.map(i => i.VendorName).filter(Boolean))].sort();
          return (
            <div>
              <div className="px-4 py-3 bg-amber-50 border-b border-amber-100">
                <p className="text-sm text-amber-800">
                  <strong>My Tasks:</strong> Invoices personally assigned to you for approval
                </p>
              </div>
              {renderPendingTable(myTasks, depts, vendors, { hideTakeover: true })}
            </div>
          );
        })()}

        {activeTab === 'history' && (
          <div>
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <p className="text-sm text-gray-700">
                <strong>History:</strong> Recently approved and rejected invoices across the company
              </p>
            </div>
            {renderHistoryTable()}
          </div>
        )}
      </div>

      {/* Invoice Approval Detail Modal */}
      {selectedInvoiceId && (
        <InvoiceApprovalDetail
          invoiceId={selectedInvoiceId}
          onClose={() => setSelectedInvoiceId(null)}
          onActionComplete={() => {
            setSelectedInvoiceId(null);
            loadData();
            if (activeTab === 'history') loadHistory();
          }}
        />
      )}

      {/* Finance Admin Takeover Modal */}
      {takeoverModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Shield className="w-5 h-5 text-purple-600" />
                Finance Admin Takeover
              </h3>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                <p className="text-sm text-purple-800">
                  You are about to take over invoice <strong>{takeoverModal.invoiceNumber}</strong> from <strong>{takeoverModal.currentApprover}</strong>.
                </p>
                <p className="text-sm text-purple-700 mt-1">
                  You will become the final approver. All subsequent approval steps will be cancelled. Department and GL assignments will be preserved.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for takeover <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={takeoverReason}
                  onChange={(e) => setTakeoverReason(e.target.value)}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="e.g. Original approver on leave, urgent payment required..."
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => { setTakeoverModal(null); setTakeoverReason(''); }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={takeoverSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={handleTakeover}
                disabled={takeoverSubmitting || !takeoverReason.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                <Shield className="w-4 h-4" />
                {takeoverSubmitting ? 'Taking Over...' : 'Confirm Takeover'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
